import { useT } from "../../../shared/i18n/i18n";
import { useQuery } from "@tanstack/react-query";
import { CircleUserRound } from "lucide-react";
import { useMemo, useState } from "react";
import { cn } from "../../../shared/lib/cn";
import { trpc, useTRPC } from "../../../shared/api/trpc";

type MyWorkItem = Awaited<ReturnType<typeof trpc.workObjects.myWork.query>>[number];

// Everything assigned to the viewer through person fields, across every
// accessible board. The daily "what's mine" answer.
export function MyWorkPanel({ onOpenItem }: { onOpenItem: (target: { boardId: string; workObjectId: string }) => void }) {
  const t = useT();
  const trpcClient = useTRPC();
  const myWorkQuery = useQuery(trpcClient.workObjects.myWork.queryOptions(undefined, { refetchOnWindowFocus: true }));
  const [hideDone, setHideDone] = useState(true);

  const groups = useMemo(() => {
    const items = (myWorkQuery.data ?? []).filter(
      (item) => !hideDone || (item.status.category !== "done" && item.status.category !== "closed"),
    );
    const byBoard = new Map<string, { boardName: string; items: MyWorkItem[] }>();
    for (const item of items) {
      const group = byBoard.get(item.boardId) ?? { boardName: item.boardName, items: [] };
      group.items.push(item);
      byBoard.set(item.boardId, group);
    }
    return [...byBoard.entries()].map(([boardId, group]) => ({ boardId, ...group }));
  }, [hideDone, myWorkQuery.data]);

  const total = groups.reduce((sum, group) => sum + group.items.length, 0);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-2 pb-4">
      <div className="flex items-center gap-3 pt-1">
        <h2 className="text-sm font-medium">{t("myWork.title")}</h2>
        <span className="text-xs tabular-nums text-muted-foreground">{total}</span>
        <div className="ml-auto flex items-center gap-0.5 rounded-md bg-muted/50 p-0.5">
          {(
            [
              { key: true, label: t("myWork.open") },
              { key: false, label: t("common.all") },
            ] as const
          ).map((entry) => (
            <button
              className={cn(
                "rounded px-1.5 py-0.5 text-[11px] transition-colors",
                hideDone === entry.key ? "bg-background text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground",
              )}
              key={entry.label}
              onClick={() => setHideDone(entry.key)}
              type="button"
            >
              {entry.label}
            </button>
          ))}
        </div>
      </div>

      {groups.length === 0 && !myWorkQuery.isLoading ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border/70 px-4 py-16 text-center">
          <CircleUserRound className="size-6 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">Nothing assigned to you{hideDone ? " and open" : ""}.</p>
          <p className="text-xs text-muted-foreground/70">{t("myWork.whatItIs")}</p>
        </div>
      ) : null}

      {groups.map((group) => (
        <section className="rounded-lg border border-border/70 bg-card" key={group.boardId}>
          <div className="flex items-baseline gap-2 px-4 pb-1 pt-3">
            <h3 className="text-sm font-medium">{group.boardName}</h3>
            <span className="text-xs tabular-nums text-muted-foreground">{group.items.length}</span>
          </div>
          <div className="flex flex-col px-2 pb-2">
            {group.items.map((item) => (
              <button
                className="flex items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent/50"
                key={`${group.boardId}:${item.workObjectId}`}
                onClick={() => onOpenItem({ boardId: group.boardId, workObjectId: item.workObjectId })}
                type="button"
              >
                <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: item.status.color }} />
                <span className="min-w-0 flex-1 truncate text-sm">{item.workObjectTitle}</span>
                {item.typeName ? <span className="shrink-0 text-xs text-muted-foreground/70">{item.typeName}</span> : null}
                <span className="shrink-0 text-xs text-muted-foreground">{item.status.name}</span>
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
